import type { PluralForms } from '../types';

/**
 * English — the source of truth.
 *
 * Its inferred type IS the `Dictionary` type the other locales are checked
 * against, so adding a key here immediately breaks `ru.ts` and `kk.ts` until they
 * carry it too. That is the point: a locale cannot silently fall behind.
 *
 * Deliberately NOT `as const` — the other locales need to supply their own
 * strings, not these exact literals.
 */
export const en = {
  app: {
    /** The build time, on the stamp's tooltip — the version itself is the label. */
    built: 'Built {date}',
    name: 'Flightplan',
    tagline: 'Operations',
  },

  theme: {
    light: 'Light theme',
    dark: 'Dark theme',
  },

  language: {
    label: 'Language',
  },

  auth: {
    username: 'Username',
    password: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    signOut: 'Sign out',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    invalidCredentials: 'Username or password is incorrect, or the account is blocked.',
    networkError: 'Cannot reach the server. Check the connection and try again.',
    serverError: 'The server could not handle the request. Try again shortly.',
    unexpectedResponse: 'The server answered in a form this app does not recognise.',
    sessionExpired: 'The session has ended. Please sign in again.',
    notPermitted: 'This screen is not available for your role.',
    role: 'Role',
    noRole: 'No role',
  },
  // One entry per screen that exists. `internationalArrivals` and `dictionaries`
  // named areas with no screen behind them and went with the session page that
  // listed them; a label for something unbuilt is a promise the app cannot keep.
  nav: {
    styleGuide: 'Design system',
    onlineTable: 'Flight schedule',
    flightMap: 'Flight map',

    internationalArrivals: 'International arrivals',
    airports: 'Airports',
    airlines: 'Airlines',
    integrationMonitor: 'Integration monitor',
    userManagement: 'Users',
    /** The header group the five administrator's screens fold into. Named for what
        the reader is looking for — the parts of the app you set up rather than
        work in — not for the role that may open them. */
    administration: 'Administration',
    profile: 'Your account',
    /** The Kibana deep link in the header — the one nav item that leaves the app.
        Named for what the reader opens, not the stack behind it — "ELK" is how
        this team says it. */
    elkDashboard: 'ELK dashboard',
    /* The burger, for screen readers — its face is an icon. */
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
  },

  profile: {
    title: 'Your account',
    lead: 'Your name and contact details, and where you change your password. Only you can edit this — an administrator edits everyone else’s from the Users screen.',
    account: 'Account',
    adminOnlyNote:
      'Your login, role and company are set by an administrator and cannot be changed here.',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Repeat new password',
    passwordMismatch: 'The two passwords do not match.',
    saved: 'Your account has been updated.',
    nothingChanged: 'Nothing has changed, so nothing was sent.',
    saveFailed: 'The server refused the change without saying which field was wrong.',
    networkError: 'Cannot reach the server. Check the connection and try again.',
    serverError: 'The server could not handle the request. Try again shortly.',
    errorLoad: 'Could not load your account. Check the connection and try again.',
    flightAccess: 'Flight access',
    flightAccessSees: 'You see',
    flightAccessAll: 'You see every flight.',
    flightAccessAdmin: 'You see every flight: an administrator’s access is never restricted.',
    flightAccessRestricted: 'You see only these flights. An administrator sets this — ask one if you need to see more.',
    flightAccessFailed: 'Could not load your flight access.',
  },

  /**
   * A flight access in words, read by the administrator who sets it and by the user it
   * narrows — the same sentence on both sides. `{airports}` is a list of cities and
   * `{airlines}` a list of carriers, each already joined with `or`.
   */
  flightAccess: {
    all: 'All flights',
    airportsOnly: 'Every flight to or from {airports}',
    airlinesOnly: 'Every {airlines} flight',
    both: '{airlines} flights to or from {airports}',
    incomplete: 'No flights — choose at least one airport or one airline',
    or: 'or',
    /** The note on the board, the map and the journal, for a restricted user only. */
    yours: 'Your flights',
    yoursHint: 'You see only these flights — the server leaves every other one out. An administrator sets this; ask one if you need to see more.',
    /** Under an empty board: a filter outside the access is not an error, just empty. */
    emptyHint: 'You see only the flights in your access, named above. A filter outside them shows nothing.',
    /** The "all" choice of a station dropdown narrowed to a reader's own airports. */
    allYourStations: 'All your airports',
  },

  airlines: {
    title: 'Airlines',
    add: 'Add airline',
    found: 'Airlines',
    searchPlaceholder: 'Code, prefix or name',
    allStates: 'All carriers',
    resetFilters: 'Clear all',
    sortHint: 'Sort by this column',
    nothingFound: 'No airline matches these filters',
    notFound: 'That airline is not in the dictionary — it may have been opened from an old link, or deleted.',
    errorLoad: 'Could not load the dictionary. Check the connection and try again.',
    errorForbidden: 'Your role may not read the airline dictionary.',
    // Named for what the flag MEANS. The JSON key is `kzFlying` and the column behind
    // it is `signed`; neither name says "on the working list", which is what it is.
    states: {
      working: 'Working list',
      notWorking: 'Off the list',
    },
    columns: {
      iata: 'IATA',
      icao: 'ICAO',
      name: 'Airline',
      prefix: 'Prefix',
      working: 'Working list',
    },
    flash: {
      created: 'Airline {code} was created.',
      saved: 'Changes to {code} were saved.',
      deleted: 'Airline {code} was deleted.',
    },
    panel: {
      create: 'New airline',
      createAction: 'Create airline',
      edit: 'Edit airline',
      noCode: 'No code',
      readOnly: 'Read-only. Only an administrator can change the airline dictionary.',
      codes: 'Codes',
      names: 'Names',
      // NOT `other`: a group containing a key called `other` structurally matches
      // `PluralForms`, so `TranslationKey` reads it as a plural set and stops
      // recursing — every key under it then stops existing. See i18n/types.ts.
      details: 'Status and notes',
      iataLength: 'Exactly {count} characters.',
      icaoLength: 'Exactly {count} characters, or leave it empty.',
      title: 'Short name',
      fullTitle: 'Legal name',
      description: 'Notes',
      saving: 'Saving…',
      saveFailed: 'The server refused the change without giving a reason.',
      forbidden: 'Only an administrator can change the airline dictionary.',
      networkError: 'Cannot reach the server. Check the connection and try again.',
      serverError: 'The server could not handle the request. Try again shortly.',
      deleteTitle: 'Delete this airline',
      deleteHint: 'The row is removed for good, and there is no active flag here. To drop a carrier from the working list without deleting it, set it to “Off the list” above.',
      deleteConfirm: 'Delete {code} permanently? Nothing cleans up after it — schedules keep the carrier code as plain text, so every flight already flown under it is left with a code that resolves to nothing.',
      deleteConfirmAction: 'Yes, delete',
      deleteFailed: 'The airline was not deleted.',
    },
  },

  internationalArrivals: {
    title: 'International arrivals',
    /** The journal is ADMIN and AAK only — the read endpoint included. */
    errorLoad: 'Could not load the journal. Check the connection and try again.',
    errorForbidden: 'Your role may not read the international arrivals journal.',
    resetFilters: 'Clear all',
    sortHint: 'Sort by this column',
    allStations: 'All airports',
    nothingFound: 'No arrival matches these filters',
    rowsFound: 'Arrivals',
    /**
     * The date filter is an upper bound, and the hint has to say so: this endpoint
     * has no range filter at all, so "up to" is the whole truth and "period" would
     * promise a day it cannot deliver.
     */
    /* The two ends of the STA range, in the `sta` column's filter cell. Each end
       is optional, so they are labelled separately rather than as one period. */
    staFrom: 'Scheduled from',
    staTo: 'Scheduled to',
    state: {
      label: 'Rows',
      active: 'Active only',
      withdrawn: 'Withdrawn only',
      all: 'Active and withdrawn',
    },
    /**
     * One entry per API field, because that is what the header shows: the field
     * name on top — the same in every language, so not translated — and this label
     * under it. The journal is read beside the API it comes from, and a value is
     * explained by following `airportScheduleId` into the schedule and the monitor,
     * both of which speak in these names.
     */
    columns: {
      airportIataCode: 'Airport',
      flt: 'Flight',
      orgn: 'Origin',
      sta: 'Scheduled Date',
      ata: 'Actual Date',
      br: 'Aircraft Arrival at Stand',
      firstPax: 'First Pax',
      lastPax: 'Last Pax',
      firstBag: 'First Bag',
      lastBag: 'Last Bag',
      ttlPax: 'Total Pax',
      ttlBag: 'Total Bag',
      spot: 'Spot',
      acType: 'AC Type',
    },
  },
  airports: {
    title: 'Airports',
    add: 'Add airport',
    found: 'Airports',
    searchPlaceholder: 'Code, airport, city or country',
    countryPlaceholder: 'Code or name',
    allStates: 'Any status',
    resetFilters: 'Clear all',
    sortHint: 'Sort by this column',
    nothingFound: 'No airport matches these filters',
    notFound: 'That airport is not on this page — it may have been opened from an old link, or removed.',
    errorLoad: 'Could not load the dictionary. Check the connection and try again.',
    errorForbidden: 'Your role may not read the airport dictionary.',
    errorSort: 'The server does not sort by that column. Pick another one.',
    states: {
      active: 'Active',
      inactive: 'Not offered',
    },
    columns: {
      iata: 'IATA',
      icao: 'ICAO',
      name: 'Airport',
      city: 'City',
      country: 'Country',
      timeZone: 'Time zone',
      status: 'Status',
      created: 'Created',
      updated: 'Updated',
    },
    flash: {
      created: 'Airport {code} was created.',
      saved: 'Changes to {code} were saved.',
      deleted: 'Airport {code} was deleted.',
    },
    panel: {
      create: 'New airport',
      createAction: 'Create airport',
      edit: 'Edit airport',
      noCode: 'No code',
      readOnly: 'Read-only. Only an administrator can change the airport dictionary.',
      codes: 'Codes',
      airportName: 'Airport name',
      place: 'Location',
      inEnglish: 'English',
      inRussian: 'Russian',
      inKazakh: 'Kazakh',
      countryCode: 'Country code',
      // The examples stay in Latin in every language: the zone ids are English.
      timeZoneSearch: 'Search: Almaty, +5',
      timeZoneNone: 'No time zone matches',
      timeZoneClear: 'Clear the time zone',
      latitude: 'Latitude',
      longitude: 'Longitude',
      saving: 'Saving…',
      missingRequired: 'Fill in the fields marked below.',
      saveFailed: 'The server refused the change without giving a reason.',
      forbidden: 'Only an administrator can change the airport dictionary.',
      networkError: 'Cannot reach the server. Check the connection and try again.',
      serverError: 'The server could not handle the request. Try again shortly.',
      deleteTitle: 'Delete this airport',
      deleteHint: 'The row is removed for good. To stop offering an airport without deleting it, set its status to “Not offered” above.',
      deleteConfirm: 'Delete {code} permanently? This cannot be undone, and every flight already matched against it keeps the code with nothing behind it.',
      deleteConfirmAction: 'Yes, delete',
      deleteFailed: 'The airport was not deleted.',
    },
  },

  users: {
    title: 'Users',
    add: 'Add user',
    found: { one: '{count} user', other: '{count} users' } as PluralForms,
    ofTotal: 'of {total}',
    searchPlaceholder: 'Login, name, email or phone',
    allRoles: 'All roles',
    allStates: 'Any status',
    resetFilters: 'Clear all',
    sortHint: 'Sort by this column',
    nothingFound: 'No user matches these filters',
    notFound: 'That user is not in the list — it may have been opened from an old link.',
    errorLoad: 'Could not load the users. Check the connection and try again.',
    errorForbidden: 'Only an administrator may read the user list.',
    allAccess: 'Any flight access',
    states: {
      active: 'Active',
      blocked: 'Blocked',
    },
    /** The access filter. What APPLIES — an administrator is always "all flights". */
    accessStates: {
      all: 'All flights',
      restricted: 'Restricted',
    },
    // Shown as written by the API rather than reworded: these are the names an
    // administrator sees in the backend too, and renaming them here would mean two
    // vocabularies for one thing.
    roles: {
      ADMIN: 'Administrator',
      AAK: 'AAK',
      USER: 'User',
      KGA_USER: 'KGA user',
      INTEGRATOR: 'Integrator',
      OPERATOR: 'Operator',
    },
    columns: {
      username: 'Login',
      name: 'Name',
      role: 'Role',
      contact: 'Contact',
      access: 'Flight access',
      status: 'Status',
      created: 'Created',
    },
    flash: {
      created: '{name} was created.',
      saved: 'Changes to {name} were saved.',
      blocked: '{name} can no longer sign in.',
      unblocked: '{name} can sign in again.',
      /** The account exists, the second write did not land: a warning, not a success. */
      createdWithoutAccess: '{name} was created, but the flight access was not saved. Until it is saved, {name} sees every flight.',
    },
    /** The flight access of one account — the panel section, and the create form. */
    access: {
      title: 'Flight access',
      all: 'All flights',
      allHint: 'Sees every flight.',
      selected: 'Only selected flights',
      selectedHint: 'Sees only the flights that match the airports and airlines below.',
      airports: 'Airports',
      airportsHint: 'The flight departs from or arrives at one of these. Empty — any airport.',
      airlines: 'Airlines',
      airlinesHint: 'The flight is operated by one of these. Empty — any airline.',
      airportSearch: 'Code or city',
      airlineSearch: 'Code or name',
      remove: 'Remove {code}',
      /** Beside a typed carrier code the working list does not have. */
      offList: 'not on the working list',
      sees: 'Sees',
      needOne: 'Choose at least one airport or one airline.',
      save: 'Save access',
      saved: 'Flight access saved.',
      nothingChanged: 'The access has not changed, so nothing was sent.',
      saveFailed: 'The server refused the access without saying why.',
      changed: 'Changed {date} by {name}',
      neverSet: 'Never set: the default, every flight.',
      adminNote: 'Administrators always see every flight. Their access cannot be restricted.',
      adminStored: 'A restriction is stored for this account — {description}. It does not apply while the role is Administrator, and applies again if the role is taken away.',
      clearStored: 'Remove the stored restriction',
      cleared: 'The stored restriction was removed.',
      /** In the list, beside an administrator who has a stored restriction. */
      adminIgnored: 'restriction ignored — administrator',
      errorLoad: 'Could not load the flight access. Check the connection and try again.',
      listFailed: 'Could not load the users’ flight access, so that column is empty and access cannot be changed. Refresh to try again.',
      retry: 'Try again',
      createNote: 'Saved right after the user is created — before you hand over the password.',
    },
    panel: {
      create: 'New user',
      createAction: 'Create user',
      edit: 'Edit user',
      details: 'Details',
      you: 'You',
      account: 'Account',
      person: 'Name',
      contact: 'Contact',
      username: 'Login',
      password: 'Password',
      passwordHint: 'At least {count} characters.',
      passwordTooShort: 'At least {count} characters.',
      newPassword: 'Reset password',
      company: 'Company',
      companyHint: 'Set at creation, and not editable afterwards.',
      chooseCompany: 'Choose a company',
      noCompanies: 'No company could be read from the existing users, and this API has no endpoint that lists them.',
      companyHead: 'Heads the company',
      role: 'Role',
      roleSelf: 'You cannot change your own role — another administrator has to do it.',
      noRole: 'No role',
      firstName: 'First name',
      middleName: 'Middle name',
      lastName: 'Last name',
      email: 'Email',
      phone: 'Phone',
      tin: 'IIN / TIN',
      /* Format rejections, told beside the field that holds the value. */
      emailInvalid: 'This does not look like an email address.',
      phoneInvalid: 'Enter a full number: +7 and ten digits.',
      tinInvalid: 'An IIN/TIN is exactly {count} digits.',
      address: 'Address',
      created: 'Created',
      updated: 'Updated',
      saving: 'Saving…',
      nothingChanged: 'Nothing has changed, so nothing was sent.',
      saveFailed: 'The server refused the change without saying which field was wrong.',
      networkError: 'Cannot reach the server. Check the connection and try again.',
      serverError: 'The server could not handle the request. Try again shortly.',
      blockTitle: 'Block this account',
      blockHint: 'The user will not be able to sign in. Nothing is deleted.',
      block: 'Block',
      blockSelf: 'You cannot block your own account.',
      unblockTitle: 'This account is blocked',
      unblockHint: 'The user cannot sign in until access is restored.',
      unblock: 'Restore access',
    },
  },

  monitor: {
    title: 'Integration monitor',
    views: {
      sources: 'Sources',
      events: 'Events',
      /**
       * What each source actually sent — a telex, an FTP file, an XML push, a CSV.
       * NOT "Deliveries": in a freight system that reads as cargo being delivered,
       * and it says nothing about what a row holds. Named for the endpoint's own
       * noun, so the "Message" link on an event points at something with a matching
       * name.
       */
      messages: 'Received messages',
    },
    errorLoad: 'Could not load the monitor. Check the connection and try again.',
    errorForbidden: 'Only an administrator may read the integration monitor.',
    errorFilter: 'The server rejected a filter — a saved link may name a source that no longer exists. Clear the filters and try again.',
    clearFilters: 'Clear all',
    /**
     * The schedule columns a source can change, as `changes[].field` names them.
     * Labels, not the API's camelCase — an unmapped field falls back to its raw
     * name rather than disappearing.
     */
    fields: {
      scheduledDate: 'Scheduled time',
      estimatedDate: 'Estimated time',
      actualDate: 'Actual time',
      departedTime: 'Left origin',
      flightStatus: 'Status',
      delayMinutes: 'Delay, min',
      delayReason: 'Delay reason',
      passengerAmount: 'Passengers',
      totalCargoWeight: 'Cargo, kg',
      planeParkingName: 'Stand',
      externalFlightId: 'External id',
      active: 'In the schedule',
    },
    changes: {
      /** The field was emptied — a word, because a blank says nothing happened. */
      cleared: 'cleared',
    },
    payloadGone: 'Retention has already removed this.',
    /**
     * Reached only by racing the 14-day clearing, because every caller checks the
     * server's own flag first. It states the absence and invents no cause — saying
     * "cleared after 14 days" for a source that simply keeps no per-flight record
     * made a working feed look like it had lost data.
     */
    payloadEmpty: 'Nothing stored for this one.',
    deliveryNoContent:
      'This delivery kept no content. A successful airport snapshot deliberately does not store its file — the per-flight record is on the event itself.',
    payloadFailed: 'Could not load it.',
    health: {
      OK: 'OK',
      STALE: 'Stale',
      FAILING: 'Failing',
      UNKNOWN: 'No data yet',
    },
    sources: {
      lead: 'Every source the application can run, delivered or not. Health is judged on reaching the source — a feed with nothing new to say is working, a feed that cannot be reached is not.',
      columns: {
        source: 'Source',
        health: 'Health',
        interval: 'Interval',
        trust: 'Trust',
        updated: 'Settings changed',
      },
      off: 'Off',
      push: 'Push',
      defaults: 'Defaults',
      seconds: '{count} s',
      minutes: '{count} min',
      replaying: 'Re-reading archive: {progress}',
      replayingStart: 'Re-reading archive…',
    },
    panel: {
      title: 'Source settings',
      displayName: 'Display name',
      enabled: 'Enabled',
      on: 'On',
      interval: 'Expected interval, seconds',
      intervalInvalid: 'A whole number of seconds, or empty.',
      resetDefault: 'default {value}',
      trust: 'Trust rank',
      trustInvalid: 'A whole number from 0 to 100.',
      updatedBy: 'Changed by {name}, {date}',
      saved: 'Saved.',
      saving: 'Saving…',
      showErrors: 'Recent errors',
      replayTitle: 'Re-read the whole archive',
      replayHint: 'Puts every archived file through today’s parsing. It only fills gaps — nothing the schedule already holds is overwritten. Five files per poll, so it takes a while, and a deploy cancels it.',
      replayStart: 'Start re-read',
      replayCancel: 'Cancel re-read',
      replayRunning: 'Running — last finished file: {progress}',
      replayRunningStart: 'Running — no file finished yet',
      networkError: 'Cannot reach the server. Check the connection and try again.',
      serverError: 'The server could not handle the request. Try again shortly.',
    },
    events: {
      found: 'Events',
      columns: {
        time: 'Time',
        type: 'Event',
        flight: 'Flight',
        /** The diff where there is one; the server's own sentence where there is not. */
        details: 'Changes',
        payload: 'Message',
      },
      types: {
        CREATED: 'Created',
        UPDATED: 'Updated',
        DELETED: 'Deleted',
        RECEIVED: 'Received',
        WARNING: 'Warning',
        ERROR: 'Error',
      },
      allSources: 'All sources',
      allTypes: 'All events',
      flightLabel: 'Flight number',
      /** An example, not the label again — the field is too narrow to repeat it. */
      flightExample: 'KC222',
      /**
       * The `airport_schedule` id — one row of the board, which is one leg of one
       * flight. "ID" rather than "row" because that is what goes in the box: a
       * number to paste, not a thing to describe. It reads against the field beside
       * it — a flight NUMBER is `KC222` and returns every leg ever flown under it,
       * an ID is `395239` and returns exactly one.
       */
      rowLabel: 'Schedule ID',
      rowExample: '395239',
      show: 'Show',
      hide: 'Hide',
      allLegs: 'Both legs',
      fieldLabel: 'Changed field',
      allFields: 'Any field',
      periodLabel: 'Period',
      defaultWindow: 'Last 48 hours',
      lastDays: 'Last {count} days',
      /** What the source itself said about this one flight. */
      sourceRecord: 'What the source sent for this flight',
      /** The whole message that record arrived in. */
      wholeDelivery: 'The full delivery it arrived in',
      window: 'Showing the last 48 hours. Filter by flight number or schedule ID to reach further back.',
      fullHistory: 'Showing the full history of this flight — not limited to the last 48 hours.',
      payload: 'Message',
      noSource: 'no source',
      nothingFound: 'No events match these filters',
    },
    messages: {
      found: 'Messages',
      lead: 'Everything the sources sent, with its original content — a telex, an FTP file, an XML push, a CSV. A poll whose content had not changed is not stored, so a quiet feed is healthy: liveness is the health light on Sources, never the row count here.',
      columns: {
        kind: 'Type',
        status: 'Status',
        rows: 'Changed / received',
        ref: 'Reference',
      },
      statuses: {
        PROCESSED: 'Processed',
        UNCHANGED: 'Unchanged',
        FAILED: 'Failed',
      },
      nothingFound: 'No messages match these filters',
    },
  },

  /** The flight map. Every position on it is worked out, not reported, and the copy
      never says otherwise: "about", "estimated", "expected" — never "is at". */
  map: {
    title: 'Flights in the air',
    mapLabel: 'Map of flights in the air',
    /** The control that brings the map back to the country after zooming out. */
    showKazakhstan: 'Kazakhstan',
    basemapFailed: 'The map outlines could not be loaded. The flights are still placed correctly.',
    inAir: { one: '{count} flight in the air', other: '{count} flights in the air' } as PluralForms,
    updatedAt: 'updated {time}',
    showingFrom: 'Showing flights as of {time}.',
    errorLoad: 'Could not refresh the flights. Check the connection — the map tries again every minute.',
    errorForbidden: 'Your role may not read the flight schedule.',
    approximate:
      'Positions are estimated from when each flight left and when it is expected — the schedule does not report where aircraft are.',
    withoutCoordinates: 'Not on the map — no coordinates in the airport dictionary: {codes}.',
    selectedGone: 'The flight in this link is no longer in the air.',
    empty: 'No flights in the air right now.',
    minutes: '{minutes} min',
    hoursMinutes: '{hours} h {minutes} min',
    inDuration: 'in {duration}',
    overdueBy: '{duration} past expected',
    backToList: 'Back to the list',
    percentFlown: '{percent}% of the way',
    departed: 'Departed',
    expected: 'Expected',
    remaining: 'Remaining',
    overdue: 'Past expected',
    awaitingLanding: 'The landing has not been reported yet.',
    /** A landed flight stays on the map, at its destination, for five minutes. */
    landed: 'Landed',
    landedAgo: 'landed {duration} ago',
    landedJustNow: 'just landed',
    /** Beside the count in the air: the flights that are on the map but on the ground. */
    landedCount: { one: '{count} landed', other: '{count} landed' } as PluralForms,
    /** Under the expected time: what it is worked out from. */
    basis: {
      LANDED: 'the reported landing',
      ESTIMATED: 'estimated arrival, as reported',
      SCHEDULED: 'from the scheduled flight time — no estimate reported',
      DISTANCE: 'from the distance — no arrival time reported',
    },
    distance: 'Distance',
    km: '{distance} km',
    registration: 'Registration',
    openInSchedule: 'Open in the flight schedule',
  },

  common: {
    date: 'Date',
    time: 'Time',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    search: 'Search',
    back: 'Back',
    refresh: 'Refresh',
    loading: 'Loading…',
    noData: 'Nothing to show',
    required: 'Required',
    yes: 'Yes',
    no: 'No',
  },

  schedule: {
    title: 'Flight schedule',
    previousPeriod: 'Previous period',
    nextPeriod: 'Next period',
    scheduledFrom: 'Scheduled from',
    scheduledTo: 'Scheduled to',
    range: {
      label: 'Period',
      today: 'Today',
      yesterday: 'Yesterday',
      tomorrow: 'Tomorrow',
      last24h: 'Last 24 hours',
      last7d: 'Last 7 days',
      next7d: 'Next 7 days',
      last30d: 'Last 30 days',
      thisMonth: 'This month',
      custom: 'Custom period',
      invalid: 'The end of the period comes before its start.',
    },
    allTypes: 'All',
    allStations: 'All stations',
    /**
     * The two exports. Both honour the filters on screen and IGNORE the pager —
     * every matching row goes into the file. They are different reports, not two
     * formats of one: the spreadsheet is ten columns for a person, APCAS is the
     * sixty-four-column interchange file another system ingests.
     */
    export: {
      label: 'Download',
      /* Named by their extensions, because that is the difference a chooser needs:
         same filters, but one file is for a person and the other for a system. */
      excel: 'Excel (.xlsx)',
      apcas: 'APCAS (.csv)',
      working: 'Preparing…',
      error: 'The export failed. Check the connection and try again.',
    },
    resetFilters: 'Clear all',
    clearFilter: 'Clear {name}',
    noMatches: 'No airline matches',
    airlinesFailed: 'The airline list could not be loaded — a code can still be typed',
    moreMatches: '{count} more — keep typing to narrow the list',
    airportsFailed: 'The airport search failed — a code can still be typed',
    searching: 'Searching…',
    laterBy: '{count} min later than scheduled',
    earlierBy: '{count} min earlier than scheduled',
    nothingFound: 'No flights match these filters',
    /** The key to the row colours, opened from the Status header. */
    statusLegend: 'Status colours',
    firstPage: 'First page',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    lastPage: 'Last page',
    sortHint: 'Sort by this column',
    perPage: 'Per page',
    pageOf: 'Page {page} of {pages}',
    /** When the total is capped, a page count would be a fiction. */
    pageOnly: 'Page {page}',
    rowsFound: 'Flights',
    bothLegsHint: 'A flight is one row per leg, so a flight number returns both its arrival and its departure.',
    errorLoad: 'Could not load the schedule. Check the connection and try again.',
    errorForbidden: 'Your role may not read the flight schedule.',
    scopes: {
      DOMESTIC: 'Domestic',
      INTERNATIONAL: 'International',
    },
    detail: {
      title: 'Flight card',
      thisStation: 'this station',
      routeAlt: 'Route {from} to {to}',
      withdrawn: 'Withdrawn',
      notFound: 'This flight is no longer in the schedule.',
      otherLeg: 'Open the other leg — {leg} at {station}',
      history: 'History',
      noHistory: 'Nothing has changed since this flight was created.',
      historyFailed: 'The history could not be loaded. The flight itself is shown above.',
      archiveNote: 'Rows from AIRPORT come from the archive kept before changes were tracked as events: they record the value as of that moment rather than the change, and name no message.',
      archivedRow: 'Archived row: written before changes were tracked as events.',
      unknownSource: 'unknown source',
      events: {
        CREATED: 'Created',
        UPDATED: 'Updated',
        DELETED: 'Deleted',
      },
      showRaw: 'Message',
      hideRaw: 'Hide',
      delayed: 'Delay {count} min',
      departedFrom: 'Left origin',
      passengers: 'Passengers',
      cargo: 'Cargo',
      kg: 'kg',
      times: { scheduled: 'Scheduled', estimated: 'Estimated', actual: 'Actual' },
      errorLoad: 'Could not load this flight. Try again.',
      errorForbidden: 'Your role may not read this flight.',
    },
    columns: {
      flight: 'Flight number',
      airline: 'Airline',
      station: 'Station',
      leg: 'Leg',
      origin: 'Departure airport',
      destination: 'Arrival airport',
      scheduled: 'Scheduled',
      estimated: 'Estimated',
      actual: 'Actual',
      status: 'Status',
      stand: 'Stand',
      /* Short, because the column holds a bare IATA code and the header is the
         widest thing in it. */
      delayReason: 'Delay code',
      passengers: 'Pax',
    },
    types: {
      ARRIVAL: 'Arrival',
      DEPARTURE: 'Departure',
    },
    statuses: {
      EXPECTED: 'Expected',
      ON_TIME: 'On time',
      DELAYED: 'Delayed',
      CHECK_IN: 'Check-in',
      BOARDING: 'Boarding',
      DEPARTED_EARLY: 'Departed early',
      DEPARTED_ON_TIME: 'Departed on time',
      DEPARTED_LATE: 'Departed late',
      LANDED_EARLY: 'Landed early',
      LANDED_ON_TIME: 'Landed on time',
      LANDED_LATE: 'Landed late',
      CANCELLED: 'Cancelled',
      UNKNOWN: 'Unknown',
    },
  },

  flight: {
    number: 'Flight',
    route: 'Route',
    scheduled: 'Scheduled',
    estimated: 'Estimated',
    actual: 'Actual',
    status: 'Status',
    // A count needs every form the language uses, and only those.
    found: { one: '{count} flight', other: '{count} flights' } as PluralForms,
    delayedBy: { one: 'Delayed by {count} minute', other: 'Delayed by {count} minutes' } as PluralForms,
  },

  // Kept here rather than taken from Intl: Chrome has no month names for
  // Kazakh and silently formats it as "2026 M07 24".
  months: {
    jan: 'January',
    feb: 'February',
    mar: 'March',
    apr: 'April',
    may: 'May',
    jun: 'June',
    jul: 'July',
    aug: 'August',
    sep: 'September',
    oct: 'October',
    nov: 'November',
    dec: 'December',
  },

  status: {
    onTime: 'On time',
    delayed: 'Delayed',
    departed: 'Departed',
    landed: 'Landed',
    cancelled: 'Cancelled',
    expected: 'Expected',
  },

  demo: {
    signedInAs: 'Signed in as {name}',
    lastUpdated: 'Last updated at {time}',
    cargo: '{weight} kg of cargo across {count} flights',
  },
};

export type Dictionary = typeof en;
